import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { legacy } from '@/shared/api/client';
import type { Passbook } from '@/shared/types';

interface PassbookContextValue {
  idPassbook: string;
  passbook: Passbook;
}

const PassbookContext = createContext<PassbookContextValue | null>(null);

export function PassbookProvider({
  idPassbook,
  passbook,
  children,
}: PassbookContextValue & { children: ReactNode }) {
  return (
    <PassbookContext.Provider value={{ idPassbook, passbook }}>{children}</PassbookContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalPassbook(): PassbookContextValue | null {
  return useContext(PassbookContext);
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePassbook(): PassbookContextValue {
  const ctx = useOptionalPassbook();
  if (!ctx) throw new Error('usePassbook deve ser usado dentro de uma rota de loja (/loja/:idPassbook)');
  return ctx;
}

/** Busca a config do tenant via proxy autenticado (com header de authz). */
// eslint-disable-next-line react-refresh/only-export-components
export function usePassbookQuery(idPassbook: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['passbook', idPassbook],
    queryFn: () =>
      legacy.get<{ passbook: Passbook }>(`/passbook/${idPassbook}`, {
        headers: { 'X-Passbook-Id': idPassbook },
      }),
    select: (data) => data.passbook,
    staleTime: 5 * 60_000,
    gcTime: 60 * 60_000,
    placeholderData: keepPreviousData,
    enabled: (options?.enabled ?? true) && Boolean(idPassbook),
  });
}
